/**
 * Centralized status and priority configuration for consistent badge styling
 * across the application. Uses Badge variants defined in components/ui/badge.tsx.
 */

import type { VariantProps } from "class-variance-authority"
import type { badgeVariants } from "@/components/ui/badge"

type BadgeVariant = VariantProps<typeof badgeVariants>["variant"]

// Priority configuration.
// `variant` drives filled Badge styling; `dotVar` is the CSS variable used by
// the quieter DotPill (muted pill + colored dot) shared by the bids table and
// the bid overview metadata strip.
export const priorityConfig = {
  critical: {
    label: "Critical",
    variant: "priority-critical" as BadgeVariant,
    dotVar: "--status-critical",
  },
  high: {
    label: "High",
    variant: "priority-high" as BadgeVariant,
    dotVar: "--status-high",
  },
  medium: {
    label: "Medium",
    variant: "priority-medium" as BadgeVariant,
    dotVar: "--status-medium",
  },
  low: {
    label: "Low",
    variant: "priority-low" as BadgeVariant,
    dotVar: "--status-low",
  },
} as const

export type Priority = keyof typeof priorityConfig

// Bid status configuration — 7-state lifecycle
export const statusConfig = {
  lead: {
    label: "Lead",
    variant: "stage-lead" as BadgeVariant,
    dotVar: "--stage-lead",
  },
  qualifying: {
    label: "Qualifying",
    variant: "stage-qualifying" as BadgeVariant,
    dotVar: "--stage-qualifying",
  },
  drafting: {
    label: "Drafting",
    variant: "stage-drafting" as BadgeVariant,
    dotVar: "--stage-drafting",
  },
  submitted: {
    label: "Submitted",
    variant: "stage-submitted" as BadgeVariant,
    dotVar: "--stage-submitted",
  },
  won: {
    label: "Won",
    variant: "stage-won" as BadgeVariant,
    dotVar: "--stage-won",
  },
  lost: {
    label: "Lost",
    variant: "stage-lost" as BadgeVariant,
    dotVar: "--stage-lost",
  },
  no_go: {
    label: "No go",
    variant: "stage-no_go" as BadgeVariant,
    dotVar: "--stage-no_go",
  },
} as const

export type BidStatus = keyof typeof statusConfig

// Helper function to get priority badge variant
export function getPriorityVariant(priority: string): BadgeVariant {
  const normalizedPriority = priority.toLowerCase() as Priority
  return priorityConfig[normalizedPriority]?.variant ?? "secondary"
}

// Helper function to get priority label
export function getPriorityLabel(priority: string): string {
  const normalizedPriority = priority.toLowerCase() as Priority
  return priorityConfig[normalizedPriority]?.label ?? priority
}

// Helper function to get the DotPill CSS variable for a priority
export function getPriorityDotVar(priority: string): string {
  const normalizedPriority = priority.toLowerCase() as Priority
  return priorityConfig[normalizedPriority]?.dotVar ?? "--muted-foreground"
}

// Helper function to get status badge variant
export function getStatusVariant(status: string): BadgeVariant {
  const normalizedStatus = status.toLowerCase() as BidStatus
  return statusConfig[normalizedStatus]?.variant ?? "secondary"
}

// Helper function to get status label
export function getStatusLabel(status: string): string {
  const normalizedStatus = status.toLowerCase() as BidStatus
  return statusConfig[normalizedStatus]?.label ?? status
}

// Helper function to get the DotPill CSS variable for a status
export function getStatusDotVar(status: string): string {
  const normalizedStatus = status.toLowerCase() as BidStatus
  return statusConfig[normalizedStatus]?.dotVar ?? "--muted-foreground"
}
